import React from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../components/layout';
import { normalizeLanguage } from '../i18n/language';
import {
  getAboutTemplate,
  ABOUT_NAMESPACE,
  ABOUT_RESOURCE_KEY,
  type AboutI18nTemplate,
} from '../i18n/templates/about';

import {
  Users,
  Milestone,
  MessageCircle,
  Rocket,
  ShieldCheck,
  Globe2,
  HeartHandshake
} from 'lucide-react';
import { cn } from '../utils/cn';

const Banner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative w-full overflow-hidden rounded-2xl flex items-center justify-center">
    <div className="absolute inset-0 z-0 animate-banner-bg bg-banner-bg bg-[length:400%_400%] scale-[2.0]" />
    <div className="relative z-10 w-full">{children}</div>
  </div>
);

const TEAM_AVATARS = [
  '/images/team/1-1.jpg',
  '/images/team/2-1.jpg',
  '/images/team/1-2.jpg',
  '/images/team/1-3.jpg',
  '/images/team/1-4.jpg',
  '/images/team/1-5.jpg',
  '/images/team/2-2.jpg',
];

const WelcomePage: React.FC = () => {
  const { i18n } = useTranslation(ABOUT_NAMESPACE);
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const text = (
    i18n.getResource(
      language,
      ABOUT_NAMESPACE,
      ABOUT_RESOURCE_KEY,
    ) as AboutI18nTemplate | undefined
  ) || getAboutTemplate(language);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-24 pb-32">
        {/* --- Hero Section --- */}
        <section className="text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-sseeaa/10 rounded-full text-sseeaa text-sm font-medium">
            <Rocket className="w-4 h-4" />
            <span>{text.ui.heroBadge}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-b tracking-tight">
            {text.ui.heroTitlePrefix}<span className="text-sseeaa">{text.ui.heroTitleAccent}</span>
          </h1>
          <p className="text-lg text-b6 max-w-2xl mx-auto leading-relaxed">
            {text.ui.heroDescription}
          </p>
        </section>

        {/* --- Introduction Section --- */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-b">{text.ui.sectionAboutTitle}</h2>
            <div className="space-y-4 text-b6 leading-relaxed">
              <p>
                {text.ui.aboutParagraph1}
              </p>
              <p>
                {text.ui.aboutParagraph2}
              </p>
              <p>
                {text.ui.aboutParagraph3}
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <div className="flex flex-col items-center gap-2 p-4 bg-w rounded-2xl shadow-sm flex-1">
                <ShieldCheck className="w-6 h-6 text-g" />
                <span className="text-xs font-bold">{text.ui.badgeVerified}</span>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 bg-w rounded-2xl shadow-sm flex-1">
                <Globe2 className="w-6 h-6 text-sseeaa" />
                <span className="text-xs font-bold">{text.ui.badgeGlobalNetwork}</span>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 bg-w rounded-2xl shadow-sm flex-1">
                <HeartHandshake className="w-6 h-6 text-[#E91E63]" />
                <span className="text-xs font-bold">{text.ui.badgeNoCommission}</span>
              </div>
            </div>
          </div>

          <div className="aspect-video bg-bgb rounded-3xl border border-b1 flex flex-col items-center justify-center p-8 gap-4 overflow-hidden relative group cursor-pointer hover:border-sseeaa transition-all">
            <div className="p-4 bg-w/50 backdrop-blur rounded-full group-hover:scale-110 transition-transform">
              <MessageCircle className="w-8 h-8 text-sseeaa" />
            </div>
            <div className="text-center z-10">
              <h4 className="font-bold text-b">{text.ui.mediaPlaceholderTitle}</h4>
              <p className="text-xs text-b4 mt-1">{text.ui.mediaPlaceholderSubtitle}</p>
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-sseeaa/5 to-transparent opacity-50" />
          </div>
        </section>

        {/* --- Team Section --- */}
        <section className="space-y-12">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-sseeaa" />
            <h2 className="text-3xl font-bold text-b">{text.ui.sectionTeamTitle}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {text.teamMembers.map((member, idx) => (
              <div key={idx} className="group bg-w p-6 rounded-3xl shadow-sm hover:shadow-glow transition-all duration-300 border border-transparent hover:border-sseeaa/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <img
                      src={TEAM_AVATARS[idx] || TEAM_AVATARS[0]}
                      alt={member.name}
                      className="w-14 h-14 rounded-2xl object-cover shadow-sm group-hover:scale-105 transition-all"
                    />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-g border-2 border-white rounded-full" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-b">{member.name}</h4>
                    <p className="text-xs text-sseeaa font-semibold uppercase tracking-wider">{member.role}</p>
                  </div>
                </div>
                <p className="text-sm text-b6 leading-relaxed">
                  {member.intro}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* --- Timeline Section --- */}
        <section className="space-y-12">
          <div className="flex items-center gap-3">
            <Milestone className="w-6 h-6 text-sseeaa" />
            <h2 className="text-3xl font-bold text-b">{text.ui.sectionMilestonesTitle}</h2>
          </div>
          <div className="relative">
            <div className="absolute left-[11px] top-4 bottom-4 w-0.5 bg-b1 md:left-1/2 md:-ml-px" />
            <div className="space-y-16">
              {text.milestones.map((block, groupIdx) => (
                <div key={groupIdx} className="relative">
                  <div className="sticky top-0 z-10 mb-8 md:text-center">
                    <span className="px-4 py-1.5 bg-b text-w rounded-full text-sm font-bold shadow-lg">
                      {block.year}
                    </span>
                  </div>
                  <div className="space-y-12">
                    {block.items.map((item, idx) => (
                      <div key={idx} className={cn(
                        "relative flex flex-col md:flex-row gap-8 items-start",
                        idx % 2 === 0 ? "md:flex-row-reverse" : ""
                      )}>
                        <div className="flex-1 w-full md:w-1/2">
                          <div className={cn(
                            "bg-w p-6 rounded-3xl shadow-sm border border-b1",
                            idx % 2 === 0 ? "md:text-right" : "md:text-left"
                          )}>
                            <span className="text-xs font-bold text-sseeaa mb-2 block">{item.date}</span>
                            <h5 className="text-lg font-bold text-b mb-2">{item.title}</h5>
                            <p className="text-sm text-b6 leading-relaxed">{item.content}</p>
                          </div>
                        </div>
                        <div className="absolute left-[11px] top-6 w-1 h-1 bg-w border-4 border-b rounded-full md:left-1/2 md:-ml-1.5 md:top-8" />
                        <div className="hidden md:block flex-1 w-1/2" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- Contact Section --- */}
        <section className="bg-b text-w rounded-[2.5rem] p-8 md:p-16 relative overflow-hidden">
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">{text.ui.sectionContactTitle}</h2>
              <p className="text-w6 text-lg">
                {text.ui.sectionContactDescription}
              </p>
              <div className="flex gap-4">
                <a
                  href="https://www.xiaohongshu.com/user/profile/6712b033000000001d020291"
                  target="_blank"
                  rel="noreferrer"
                  className="px-6 py-3 bg-[#ff2442] hover:bg-[#d91f38] text-white rounded-2xl font-bold transition-all flex items-center gap-2"
                >
                  <HeartHandshake className="w-5 h-5" />
                  {text.ui.actionXiaohongshu}
                </a>
              </div>
            </div>
            <div className="flex flex-col items-center gap-6 md:items-end w-full">
              <button
                onClick={() => window.open('https://work.weixin.qq.com/ca/cawcdef7cd05264c7a', '_blank')}
                className="group relative flex items-center gap-4 p-6 bg-w/10 hover:bg-w/20 rounded-3xl border border-w/20 transition-all w-full max-w-sm"
              >
                <div className="p-3 bg-sseeaa rounded-2xl">
                  <MessageCircle className="w-8 h-8 text-w" />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-lg">{text.ui.actionAddWechat}</h4>
                  <p className="text-w4 text-sm">{text.ui.assistantName}</p>
                </div>
                <div className="ml-auto w-8 h-8 rounded-full bg-w/10 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  →
                </div>
              </button>
            </div>
          </div>
          {/* Decorative Background */}
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-sseeaa/20 rounded-full blur-3xl" />
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-g/20 rounded-full blur-2xl" />
        </section>
      </div>
    </AppLayout>
  );
};

export default WelcomePage;
